import { useState, useMemo, createContext, useContext, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { useAuth } from '../Auth';
import API from '../../API';

const OrganizationContext = createContext();

export const useOrganization = () => useContext(OrganizationContext);

export const OrganizationContextProvider = ({ children }) => {
    const { userObj, activeOrgId, setActiveOrgId, currentUser, isRegistering } = useAuth();
    
    // All organizations the user has access to (fetched once)
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [error, setError] = useState(null);

    // Organization members for the active organization (real-time)
    const [orgMembers, setOrgMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    // Active invites for the organization (real-time)
    const [orgInvites, setOrgInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(false);

    // Organization metadata for the active organization (fetched once on org switch)
    const [orgMetadata, setOrgMetadata] = useState({});
    const [loadingMetadata, setLoadingMetadata] = useState(false);

    // Fetch all organizations the user is a member of (one-time call)
    const fetchAllOrganizations = useCallback(async (orgIds) => {
        if (!orgIds || orgIds.length === 0) {
            setOrganizations([]);
            return;
        }

        setLoadingOrgs(true);
        setError(null);

        try {
            // Firestore 'in' queries have a limit of 10, so batch if needed
            const batchSize = 10;
            const batches = [];

            for (let i = 0; i < orgIds.length; i += batchSize) {
                const batch = orgIds.slice(i, i + batchSize);
                batches.push(batch);
            }

            const allOrgs = [];

            for (const batch of batches) {
                const orgsQuery = query(
                    collection(firestore, 'organizations'),
                    where('__name__', 'in', batch)
                );

                const snapshot = await getDocs(orgsQuery);
                snapshot.forEach(doc => {
                    allOrgs.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
            }

            setOrganizations(allOrgs);
        } catch (err) {
            console.error('Error fetching organizations:', err);
            setError(err.message);
            setOrganizations([]);
        } finally {
            setLoadingOrgs(false);
        }
    }, []);

    // Fetch all organizations when user logs in or org_ids change
    useEffect(() => {
		if(isRegistering) return;
        if (userObj?.org_ids?.length > 0) {
            fetchAllOrganizations(userObj.org_ids);
            return;
        }

        if (userObj && Array.isArray(userObj.org_ids)) {
            setOrganizations([]);
        }
    }, [userObj, fetchAllOrganizations, isRegistering]);

    // Real-time listeners for each organization
    useEffect(() => {
        if (isRegistering || !userObj?.org_ids || userObj.org_ids.length === 0) {
            return;
        }

        const unsubscribers = [];

		const validOrgIds = userObj.org_ids.filter(id => !!id);
        // Set up a listener for each organization
        validOrgIds.forEach(orgId => {
            const orgRef = collection(firestore, 'organizations');
            const orgQuery = query(orgRef, where('__name__', '==', orgId));

            const unsubscribe = onSnapshot(
                orgQuery,
                (snapshot) => {
                    if (!snapshot.empty) {
                        const updatedOrg = {
                            id: snapshot.docs[0].id,
                            ...snapshot.docs[0].data()
                        };

                        // Update the organization in the organizations array
                        setOrganizations(prev => {
                            const index = prev.findIndex(org => org.id === orgId);
                            if (index !== -1) {
                                const updated = [...prev];
                                updated[index] = updatedOrg;
                                return updated;
                            }
                            return prev;
                        });
                    }
                },
                (err) => {
                    console.error(`Error listening to organization ${orgId}:`, err);
                }
            );

            unsubscribers.push(unsubscribe);
        });

        // Cleanup all listeners when org_ids change
        return () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
        };
    }, [userObj?.org_ids, isRegistering]);
    
    // Real-time listener for organization members
    useEffect(() => {
        if (!activeOrgId || !currentUser || isRegistering) {
            setOrgMembers([]);
            setLoadingMembers(false);
            return;
        }

        setLoadingMembers(true);

        // Set up real-time listener for organization's users subcollection
        const membersRef = collection(firestore, 'organizations', activeOrgId, 'members');

        const unsubscribe = onSnapshot(
            membersRef,
            (snapshot) => {
                const members = [];
                snapshot.forEach(doc => {
                    members.push({
                        uid: doc.id,
                        ...doc.data()
                    });
                });

                setOrgMembers(members);
                setLoadingMembers(false);
            },
            (err) => {
                console.error('Error fetching organization members:', err);
                setOrgMembers([]);
                setLoadingMembers(false);
            }
        );

        return () => unsubscribe();
    }, [activeOrgId, currentUser, isRegistering]);

    // Real-time listener for active organization invites
    useEffect(() => {
        if (!activeOrgId || !currentUser || isRegistering) {
            setOrgInvites([]);
            setLoadingInvites(false);
            return;
        }

        setLoadingInvites(true);

        const invitesRef = collection(firestore, 'invitations');
        const invitesQuery = query(
            invitesRef,
            where('org_id', '==', activeOrgId),
            where('accepted_at', '==', null),
            where('invalidated_at', '==', null)
        );

        const unsubscribe = onSnapshot(
            invitesQuery,
            (snapshot) => {
                const now = Date.now();
                const invites = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(invite => invite.expiresAt > now);

                setOrgInvites(invites);
                setLoadingInvites(false);
            },
            (err) => {
                console.error('Error fetching organization invites:', err);
                setOrgInvites([]);
                setLoadingInvites(false);
            }
        );

        return () => unsubscribe();
    }, [activeOrgId, currentUser, isRegistering]);

    // Get current user's organization data
    const currentUserOrgData = useMemo(() => {
        if (!currentUser?.uid || !orgMembers.length) return null;
        return orgMembers.find(member => member.uid === currentUser.uid) || null;
    }, [orgMembers, currentUser?.uid]);

    // Fetch organization metadata when org switches
    useEffect(() => {
        if (!activeOrgId || !currentUser || isRegistering) {
            setOrgMetadata({});
            setLoadingMetadata(false);
            return;
        }

        setLoadingMetadata(true);

        (async () => {
            try {
                const response = await API.getOrgMetadata(activeOrgId);
                const payload = response?.data || {};
                if (payload.metadata) {
                    setOrgMetadata({ ...payload.metadata, active: payload.active });
                } else {
                    setOrgMetadata({ active: payload.active });
                }
            } catch (err) {
                console.error('Error fetching organization metadata:', err);
                setOrgMetadata({});
            } finally {
                setLoadingMetadata(false);
            }
        })();
    }, [currentUser, activeOrgId, currentUserOrgData?.role, isRegistering]);

    // Debug log removed
    // Switch to a different organization
    const switchOrganization = useCallback((orgId) => {
        if (!orgId) {
            console.error('Cannot switch to null or undefined organization');
            return;
        }

        // Verify the user has access to this org
        if (!userObj?.org_ids?.includes(orgId)) {
            console.error('User does not have access to organization:', orgId);
            return;
        }

        // Update the active org ID - this will trigger listener updates in Auth context
        setActiveOrgId(orgId);

        try {
            window.localStorage.setItem('activeOrgId', JSON.stringify(orgId));
        } catch (err) {
            console.error('Failed to persist activeOrgId to localStorage:', err);
        }
    }, [userObj?.org_ids, setActiveOrgId]);

    // Get the currently active organization object
    const activeOrganization = useMemo(() => {
        return organizations.find(org => org.id === activeOrgId) || null;
    }, [organizations, activeOrgId]);

    // Refresh organizations data (call this after org updates)
    // Can optionally pass specific orgIds to fetch, otherwise uses userObj.org_ids
    const refreshOrganizations = useCallback(async (explicitOrgIds = null) => {
        const orgIdsToFetch = explicitOrgIds || userObj?.org_ids;
        if (orgIdsToFetch) {
            await fetchAllOrganizations(orgIdsToFetch);
        }
    }, [userObj?.org_ids, fetchAllOrganizations]);
    // Add a single organization to the list (for immediate use after creation)
    const addOrganization = useCallback(async (orgId) => {
        if (!orgId) return;

        try {
            // Fetch the single org from Firestore
            const orgDoc = await getDocs(
                query(collection(firestore, 'organizations'), where('__name__', '==', orgId))
            );

            if (!orgDoc.empty) {
                const newOrg = {
                    id: orgDoc.docs[0].id,
                    ...orgDoc.docs[0].data()
                };

                // Add to organizations list if not already present
                setOrganizations(prev => {
                    const exists = prev.some(org => org.id === orgId);
                    if (exists) return prev;
                    return [...prev, newOrg];
                });
            }
        } catch (err) {
            console.error('Error adding organization:', err);
        }
    }, []);

    const value = useMemo(() => ({
        organizations,
        activeOrganization,
        activeOrgId,
        orgMembers,
        orgInvites,
        currentUserOrgData,
        loadingMembers,
        loadingInvites,
        orgMetadata,
        loadingMetadata,
        switchOrganization,
        refreshOrganizations,
        addOrganization,
        loadingOrgs,
        error
    }), [
        organizations,
        activeOrganization,
        activeOrgId,
        orgMembers,
        orgInvites,
        currentUserOrgData,
        loadingMembers,
        loadingInvites,
        orgMetadata,
        loadingMetadata,
        switchOrganization,
        refreshOrganizations,
        addOrganization,
        loadingOrgs,
        error
    ]);

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};
